/**
 * useQueryRefinement · Hook owning the five refinement handlers.
 *
 * Rehomes the following handlers from KnowledgeGraphPage.jsx (pre-extraction
 * line numbers in parentheses):
 *   - fetchRefinement       (1140)  · call POST /api/query/refine and push
 *                                     an inline clarification prompt into
 *                                     the query thread when the backend says
 *                                     needs_refinement=true.
 *   - toggleRefineOption    (1193)  · toggle a chip selection inside an inline
 *                                     clarification_prompt thread item.
 *   - submitRefinedQuery    (1213)  · user confirms · pass selections as
 *                                     structured metadata to handleQuery.
 *   - advanceRefinePrompt   (1250)  · multi-round chip presentation ·
 *                                     advance to the next facet.
 *   - skipRefinePrompt      (1261)  · user dismisses · fire the original
 *                                     query as typed.
 *
 * The refinement flow does not own any state of its own · every read and
 * write goes through the parent's shared queryThread. That is intentional ·
 * the clarification-prompt state lives INSIDE a thread item so it flows
 * through the same thread-rendering pipeline as any other answer.
 *
 * Extracted 2026-08-01 · IJ-245 · Phase 5D-1 of the KnowledgeGraphPage.jsx
 * split (see /app/memory/design_drafts/kg_page_split_map.md Section Three
 * Boundary Three · sub-slice 5D-1). Phase 5D-2 will extract the remaining
 * eight query-orchestrator handlers.
 * Umbrella: IJ-076 Predictive Text Rebuild continuation · Refactor
 * Programme 2026.
 * Owner: E1.
 *
 * Usage:
 *   const {
 *     fetchRefinement,
 *     toggleRefineOption,
 *     submitRefinedQuery,
 *     advanceRefinePrompt,
 *     skipRefinePrompt,
 *   } = useQueryRefinement({
 *     queryThread, setQueryThread, setQuery,
 *     refineRequestedQueries, setRefineRequestedQueries,
 *     handleQuery,
 *   });
 *
 * Every handler body is transposed byte-verbatim from the pre-extraction
 * file. No behaviour change. Every axios URL preserved. Every toast copy
 * preserved.
 */
import { API, authAxios } from '../utils/authClient';

export const useQueryRefinement = ({
  queryThread,
  setQueryThread,
  setQuery,
  refineRequestedQueries,
  setRefineRequestedQueries,
  handleQuery,
}) => {
  // Instantiate the authed axios client once. authAxios is a factory; calling
  // it returns an axios instance with the Bearer-token interceptor wired in.
  // Fixes 'api.post is not a function' crash — same regression as
  // useUploadFlow.js line 45. Root-caused 2026-08-11 during upload sweep.
  const api = authAxios();
  // Session 3.6 — Call /api/query/refine.
  //   fromEnterKey=true  → user committed; push clarification UI into the thread.
  //   fromEnterKey=false → pre-warm only; do NOT push UI, do NOT clear input.
  //
  // Dedup: we mark the query as in-flight SYNCHRONOUSLY before the await so a
  // racing Enter-press can't fire a second identical request. (D2 fix from
  // May 2026 — duplicate clarification panels were appearing because both
  // the debounce and the Enter handlers passed the dedup check.)
  const fetchRefinement = async (q, fromEnterKey) => {
    const trimmed = (q || '').trim();
    if (trimmed.length < 4) return false;
    // D4.1 fix (Jun 11 2026) — Only the pre-warm path respects the dedup
    // set. The Enter path bypasses it so an in-flight pre-warm can never
    // short-circuit the Enter-press → the user always sees clarification
    // chips when the backend says needs_refinement=true.
    if (!fromEnterKey && refineRequestedQueries.has(trimmed)) return false;
    // Mark in-flight BEFORE awaiting so a concurrent caller bails immediately.
    setRefineRequestedQueries(prev => { const next = new Set(prev); next.add(trimmed); return next; });
    try {
      const resp = await api.post(`${API}/query/refine`, {
        query: trimmed,
        // Enter path → allow LLM fallback (worth the latency before firing).
        // Typing-pause path → heuristics only, cheap.
        allow_llm_fallback: !!fromEnterKey,
      });
      if (resp.data?.needs_refinement && Array.isArray(resp.data.facets) && resp.data.facets.length > 0) {
        if (!fromEnterKey) {
          // Pre-warm only — we already have the response cached on the server
          // (and on the dedup set). Do nothing UI-side; the next Enter-press
          // will re-call and we'll fall through this branch with fromEnterKey=true.
          // But we DON'T mark it in the dedup set as "done" — we WANT Enter to
          // re-fetch and surface the panel. Roll the dedup back.
          setRefineRequestedQueries(prev => { const next = new Set(prev); next.delete(trimmed); return next; });
          return false;
        }
        // Enter path: push an inline clarification prompt into the thread.
        setQueryThread(prev => [...prev, {
          query: trimmed,
          result: {
            type: 'clarification_prompt',
            original_query: trimmed,
            facets: resp.data.facets,
            selections: {},
            // D4 (Jun 15 2026) — Multi-round: render ONE chip group at a time.
            // Advance index on "Continue"; submit when last facet answered.
            active_facet_index: 0,
            resolved: false,
          },
        }]);
        setQuery('');
        return true;
      }
    } catch (e) {
      // Non-fatal — roll back the in-flight marker so a retry is allowed.
      setRefineRequestedQueries(prev => { const next = new Set(prev); next.delete(trimmed); return next; });
    }
    return false;
  };

  // User toggles a chip inside an inline clarification_prompt thread item.
  // Updates the selections on that specific thread item (by index).
  const toggleRefineOption = (threadIdx, facet, optionLabel) => {
    setQueryThread(prev => prev.map((it, i) => {
      if (i !== threadIdx || it.result?.type !== 'clarification_prompt' || it.result.resolved) return it;
      const current = it.result.selections?.[facet.key] || [];
      const nextSelections = { ...(it.result.selections || {}) };
      if (facet.multi_select) {
        nextSelections[facet.key] = current.includes(optionLabel)
          ? current.filter(x => x !== optionLabel)
          : [...current, optionLabel];
      } else {
        nextSelections[facet.key] = current.includes(optionLabel) ? [] : [optionLabel];
      }
      return { ...it, result: { ...it.result, selections: nextSelections } };
    }));
  };

  // User confirms — pass selections as STRUCTURED METADATA (not concatenated
  // into the query string). The raw query stays verbatim so the downstream
  // Brave search and LLM drafter aren't bloated. (D4 Jun 15 2026 — root-cause
  // fix for the ~10 s → >90 s latency spike caught before the board demo.)
  const submitRefinedQuery = (threadIdx) => {
    const item = queryThread[threadIdx];
    if (!item || item.result?.type !== 'clarification_prompt') return;
    const { original_query, facets, selections, custom_time_scope } = item.result;
    const refinementFacets = {};
    for (const f of (facets || [])) {
      const rawPicked = (selections?.[f.key] || []);
      // D3 (Jun 11 2026) — on the time_scope facet, the 'other' chip is a
      // gateway to free-text. If picked AND a custom value was typed, swap
      // the 'other' label for the typed value so the LLM sees the actual window.
      let picked = rawPicked;
      if (f.key === 'time_scope' && custom_time_scope && custom_time_scope.trim()) {
        picked = rawPicked.map(l => {
          const lower = (l || '').toLowerCase();
          if (lower.includes('custom') || lower === 'other' || lower.startsWith('or enter')) {
            return custom_time_scope.trim();
          }
          return l;
        });
      } else {
        picked = rawPicked.filter(l =>
          l && l.toLowerCase() !== 'other' && l.toLowerCase() !== 'something else?' && l.toLowerCase() !== 'or something else?'
        );
      }
      if (picked.length) refinementFacets[f.key] = picked;
    }
    // Mark the clarification item as resolved + record the facet selections for display
    setQueryThread(prev => prev.map((it, i) => i === threadIdx
      ? { ...it, result: { ...it.result, resolved: true, refinement_facets: refinementFacets } }
      : it
    ));
    // Pass facets as metadata — query string stays clean for Brave.
    handleQuery(original_query, false, refinementFacets);
  };

  // D4 (Jun 15 2026) — Multi-round chip presentation: advance to the next
  // facet instead of submitting. Renders only one chip group at a time.
  const advanceRefinePrompt = (threadIdx) => {
    setQueryThread(prev => prev.map((it, i) => {
      if (i !== threadIdx || it.result?.type !== 'clarification_prompt' || it.result.resolved) return it;
      const total = (it.result.facets || []).length;
      const nextIdx = (it.result.active_facet_index || 0) + 1;
      if (nextIdx >= total) return it; // caller should have invoked submitRefinedQuery instead
      return { ...it, result: { ...it.result, active_facet_index: nextIdx } };
    }));
  };

  // User dismisses — fire the original query as typed.
  const skipRefinePrompt = (threadIdx) => {
    const item = queryThread[threadIdx];
    if (!item || item.result?.type !== 'clarification_prompt') return;
    setQueryThread(prev => prev.map((it, i) => i === threadIdx
      ? { ...it, result: { ...it.result, resolved: true, skipped: true } }
      : it
    ));
    handleQuery(item.result.original_query, false);
  };

  return {
    fetchRefinement,
    toggleRefineOption,
    submitRefinedQuery,
    advanceRefinePrompt,
    skipRefinePrompt,
  };
};

export default useQueryRefinement;
